import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Inbox, CheckCircle2, XCircle, AlertTriangle, Mail, ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import { listEmails, emailsStats } from "@/lib/admin-api";
import { formatMtyDateTimeShort, formatMtyRelative } from "@/lib/mty-date";
import { toast } from "sonner";

const PAGE = 50;

const STATUS_LABELS = {
  sent: { l: "Entregado", cls: "bg-green-100 text-green-800 border-green-200", icon: CheckCircle2 },
  failed: { l: "Fallo", cls: "bg-red-100 text-red-800 border-red-200", icon: XCircle },
  skipped: { l: "Omitido", cls: "bg-neutral-100 text-neutral-700 border-neutral-200", icon: AlertTriangle },
};

export default function AdminEmails() {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [emailType, setEmailType] = useState("");
  const [status, setStatus] = useState("");
  const [labels, setLabels] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [st, lst] = await Promise.all([
        emailsStats(),
        listEmails({ limit: PAGE, skip, email_type: emailType, status }),
      ]);
      setStats(st);
      setItems(lst.items || []);
      setTotal(lst.total || 0);
      setLabels(lst.type_labels || {});
    } catch {
      toast.error("Error cargando correos");
    } finally {
      setLoading(false);
    }
  }, [skip, emailType, status]);

  useEffect(() => { load(); }, [load]);

  const page = Math.floor(skip / PAGE) + 1;
  const pages = Math.max(1, Math.ceil(total / PAGE));

  return (
    <div className="p-5 md:p-8 max-w-7xl">
      <div className="mb-6">
        <h1 className="section-title text-3xl md:text-4xl font-black flex items-center gap-3">
          <Inbox className="w-8 h-8 text-red-600" /> Registro de correos
        </h1>
        <p className="text-neutral-500 text-sm mt-1">Todos los emails enviados por el sistema · <span className="text-neutral-400">Horario: Monterrey (CST, UTC-6)</span></p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard icon={Mail} color="from-blue-500 to-blue-600" label="Total enviados" value={stats.total} />
          <StatCard icon={CheckCircle2} color="from-green-500 to-green-600" label="Entregados" value={stats.sent} />
          <StatCard icon={XCircle} color="from-red-500 to-red-600" label="Fallidos" value={stats.failed} />
          <StatCard icon={AlertTriangle} color="from-neutral-500 to-neutral-600" label="Omitidos" value={stats.skipped} />
        </div>
      )}

      {/* Type distribution */}
      {stats && (
        <div className="bg-white border border-neutral-200 rounded-2xl p-5 mb-4">
          <h3 className="font-bold text-base mb-3">Distribución por tipo de correo</h3>
          <div className="flex flex-wrap gap-2">
            {Object.entries(stats.type_counts).map(([k, v]) => (
              <span key={k} className="text-xs font-semibold px-3 py-1.5 rounded-full border bg-neutral-50 border-neutral-200 text-neutral-700">
                {stats.type_labels?.[k] || k}: <strong>{v}</strong>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-neutral-200 rounded-2xl p-4 mb-4 flex flex-col md:flex-row gap-2">
        <select value={emailType} onChange={(e) => { setEmailType(e.target.value); setSkip(0); }} className="form-input" data-testid="filter-email-type">
          <option value="">Todos los tipos</option>
          {Object.entries(labels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={status} onChange={(e) => { setStatus(e.target.value); setSkip(0); }} className="form-input" data-testid="filter-email-status">
          <option value="">Todos los estados</option>
          <option value="sent">Entregados</option>
          <option value="failed">Fallidos</option>
          <option value="skipped">Omitidos</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white border border-neutral-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="emails-table">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr className="text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider">
                <th className="px-4 py-3">Fecha</th>
                <th className="px-4 py-3">Tipo</th>
                <th className="px-4 py-3">Destinatario</th>
                <th className="px-4 py-3">Asunto</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3">Origen</th>
                <th className="px-4 py-3">Lead</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-neutral-400">Cargando...</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-neutral-400">Sin correos con esos filtros.</td></tr>
              ) : items.map((e) => {
                const stInfo = STATUS_LABELS[e.status] || { l: e.status, cls: "bg-neutral-100 text-neutral-700 border-neutral-200", icon: Mail };
                const StIcon = stInfo.icon;
                return (
                  <tr key={e.id} className="hover:bg-neutral-50">
                    <td className="px-4 py-3 text-neutral-500 whitespace-nowrap">
                      <div>{formatMtyDateTimeShort(e.created_at)}</div>
                      <div className="text-[10px] text-neutral-400">{formatMtyRelative(e.created_at)}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-semibold text-neutral-900">{labels[e.email_type] || e.email_type}</span>
                    </td>
                    <td className="px-4 py-3 text-neutral-700 font-mono text-xs">{e.to}</td>
                    <td className="px-4 py-3 text-neutral-600 max-w-xs truncate" title={e.subject}>{e.subject}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold border ${stInfo.cls}`}>
                        <StIcon className="w-3 h-3" /> {stInfo.l}
                      </span>
                      {e.error && <p className="text-[10px] text-red-500 mt-1 max-w-xs truncate" title={e.error}>{e.error}</p>}
                    </td>
                    <td className="px-4 py-3 text-xs text-neutral-600">
                      {e.triggered_by === "system" ? (
                        <span className="text-blue-700 font-semibold">Automático</span>
                      ) : (
                        <span className="text-purple-700 font-semibold">Manual ({e.triggered_by})</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {e.lead_id ? (
                        <Link to={`/admin/leads/${e.lead_id}`} className="text-red-600 hover:text-red-700 inline-flex items-center gap-1 text-xs font-semibold">
                          Ver <ExternalLink className="w-3 h-3" />
                        </Link>
                      ) : <span className="text-neutral-400 text-xs">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {total > PAGE && (
          <div className="px-4 py-3 border-t border-neutral-100 flex items-center justify-between text-sm">
            <span className="text-neutral-500">Página {page} de {pages} · {total} correos</span>
            <div className="flex gap-1">
              <button onClick={() => setSkip(Math.max(0, skip - PAGE))} disabled={skip === 0} className="p-2 rounded-lg hover:bg-neutral-100 disabled:opacity-40">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button onClick={() => setSkip(skip + PAGE)} disabled={skip + PAGE >= total} className="p-2 rounded-lg hover:bg-neutral-100 disabled:opacity-40">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, color, label, value }) {
  return (
    <div className="bg-white rounded-2xl border border-neutral-200 p-4">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center flex-shrink-0`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="text-xs text-neutral-500 font-medium">{label}</p>
          <p className="text-2xl font-black tracking-tight section-title">{value}</p>
        </div>
      </div>
    </div>
  );
}
